import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    Paper,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert
} from '@mui/material';
import RichTextInput from './RichTextInput';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    AutoAwesome as AiIcon
} from '@mui/icons-material';
import QuizGenerator from '../QuizGenerator';

interface DragDropEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface DraggableItem {
    id: string;
    text: string;
    correctZone: string;
}

interface DropZone {
    id: string;
    label: string;
    description?: string;
}

const DragDropEditor: React.FC<DragDropEditorProps> = ({ content, onChange }) => {
    const [generatorOpen, setGeneratorOpen] = useState(false);
    const instruction = content?.instruction || '';
    const items: DraggableItem[] = content?.items || [];
    const zones: DropZone[] = content?.zones || [];
    const layout = content?.layout || 'vertical'; // vertical, horizontal, grid

    const addItem = () => {
        const newItem: DraggableItem = {
            id: Date.now().toString(),
            text: '',
            correctZone: zones[0]?.id || ''
        };
        onChange({ ...content, items: [...items, newItem] });
    };

    const handleQuestionsGenerated = (generatedQuestions: any[]) => {
        const generatedZones: DropZone[] = [];
        const generatedItems: DraggableItem[] = [];

        generatedQuestions.forEach((gq, zoneIndex) => {
            if (!gq.zone || !Array.isArray(gq.items) || gq.items.length === 0) {
                return;
            }

            const zoneId = `${Date.now()}-z${zoneIndex}`;
            generatedZones.push({ id: zoneId, label: String(gq.zone), description: '' });

            gq.items.forEach((item: any, itemIndex: number) => {
                generatedItems.push({
                    id: `${Date.now()}-z${zoneIndex}i${itemIndex}`,
                    text: String(item),
                    correctZone: zoneId
                });
            });
        });

        if (generatedZones.length === 0) {
            return;
        }

        onChange({
            ...content,
            zones: [...zones, ...generatedZones],
            items: [...items, ...generatedItems]
        });
    };

    const updateItem = (itemId: string, field: string, value: any) => {
        const updatedItems = items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
        );
        onChange({ ...content, items: updatedItems });
    };

    const deleteItem = (itemId: string) => {
        const updatedItems = items.filter(item => item.id !== itemId);
        onChange({ ...content, items: updatedItems });
    };

    const addZone = () => {
        const newZone: DropZone = {
            id: Date.now().toString(),
            label: '',
            description: ''
        };
        onChange({ ...content, zones: [...zones, newZone] });
    };

    const updateZone = (zoneId: string, field: string, value: any) => {
        const updatedZones = zones.map(zone =>
            zone.id === zoneId ? { ...zone, [field]: value } : zone
        );
        onChange({ ...content, zones: updatedZones });
    };

    const deleteZone = (zoneId: string) => {
        const updatedZones = zones.filter(zone => zone.id !== zoneId);

        // Update items that reference the deleted zone to point to the first available zone
        const updatedItems = items.map(item => {
            if (item.correctZone === zoneId && updatedZones.length > 0) {
                return { ...item, correctZone: updatedZones[0].id };
            }
            return item;
        });

        onChange({ ...content, zones: updatedZones, items: updatedItems });
    };

    // Validation checks
    const zoneIds = new Set(zones.map(zone => zone.id));
    const invalidItems = items.filter(item => !zoneIds.has(item.correctZone));
    const hasValidationError = invalidItems.length > 0 || (items.length > 0 && zones.length === 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Drag & Drop Aufgabe</Typography>
                <Button
                    variant="outlined"
                    startIcon={<AiIcon />}
                    onClick={() => setGeneratorOpen(true)}
                >
                    KI-Generator
                </Button>
            </Box>

            <Alert severity="info">
                Lernende ziehen Elemente per Drag & Drop in die richtigen Bereiche.
                Die Bewertung erfolgt prozentual basierend auf der Anzahl korrekt zugeordneter Elemente.
            </Alert>

            {hasValidationError && (
                <Alert severity="error">
                    <Typography variant="subtitle2" gutterBottom>Validierungsfehler:</Typography>
                    {zones.length === 0 && items.length > 0 && (
                        <Typography variant="body2">• Es müssen Drop-Zonen definiert werden, bevor Elemente hinzugefügt werden können.</Typography>
                    )}
                    {invalidItems.length > 0 && (
                        <Typography variant="body2">• {invalidItems.length} Element(e) verweisen auf nicht existierende Drop-Zonen. Bitte überprüfen Sie die Zuordnungen.</Typography>
                    )}
                </Alert>
            )}

            {/* Instruction */}
            <RichTextInput
                label="Aufgabenstellung"
                value={instruction}
                onChange={(val) => onChange({ ...content, instruction: val })}
                placeholder="Ordnen Sie die Elemente den richtigen Kategorien zu..."
            />

            {/* Layout Selection */}
            <FormControl fullWidth>
                <InputLabel>Layout der Drop-Zonen</InputLabel>
                <Select
                    value={layout}
                    label="Layout der Drop-Zonen"
                    onChange={(e) => onChange({ ...content, layout: e.target.value })}
                >
                    <MenuItem value="vertical">Vertikal (untereinander)</MenuItem>
                    <MenuItem value="horizontal">Horizontal (nebeneinander)</MenuItem>
                    <MenuItem value="grid">Raster (2x2 oder mehr)</MenuItem>
                </Select>
            </FormControl>

            {/* Drop Zones */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Drop-Zonen</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addZone} size="small">
                        Zone hinzufügen
                    </Button>
                </Box>

                {zones.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Drop-Zonen definiert
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {zones.map((zone, index) => (
                            <Paper key={zone.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <Chip label={`Zone ${index + 1}`} color="primary" size="small" />
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <TextField
                                            label="Bezeichnung"
                                            size="small"
                                            fullWidth
                                            required
                                            value={zone.label}
                                            onChange={(e) => updateZone(zone.id, 'label', e.target.value)}
                                            placeholder="z.B. Kategorie A"
                                        />
                                        <TextField
                                            label="Beschreibung (optional)"
                                            size="small"
                                            fullWidth
                                            value={zone.description || ''}
                                            onChange={(e) => updateZone(zone.id, 'description', e.target.value)}
                                            placeholder="Zusätzliche Erklärung..."
                                        />
                                    </Box>
                                    <IconButton
                                        onClick={() => deleteZone(zone.id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Draggable Items */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Ziehbare Elemente</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addItem}
                        size="small"
                        disabled={zones.length === 0}
                    >
                        Element hinzufügen
                    </Button>
                </Box>

                {zones.length === 0 && (
                    <Alert severity="warning">
                        Bitte erstellen Sie zuerst Drop-Zonen, bevor Sie Elemente hinzufügen.
                    </Alert>
                )}

                {items.length === 0 && zones.length > 0 && (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Elemente definiert
                    </Typography>
                )}

                {items.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map((item, index) => (
                            <Paper key={item.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <DragIcon sx={{ color: 'text.secondary', mt: 1 }} />
                                    <Chip label={`Element ${index + 1}`} size="small" />
                                    <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
                                        <TextField
                                            label="Text"
                                            size="small"
                                            fullWidth
                                            required
                                            value={item.text}
                                            onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                                            placeholder="z.B. Apfel"
                                        />
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Richtige Zone</InputLabel>
                                            <Select
                                                value={item.correctZone}
                                                label="Richtige Zone"
                                                onChange={(e) => updateItem(item.id, 'correctZone', e.target.value)}
                                            >
                                                {zones.map(zone => (
                                                    <MenuItem key={zone.id} value={zone.id}>
                                                        {zone.label || 'Unbenannt'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <IconButton
                                        onClick={() => deleteItem(item.id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Summary */}
            {zones.length > 0 && items.length > 0 && (
                <Alert severity="success">
                    {zones.length} Drop-Zone(n) und {items.length} Element(e) definiert.
                    Die minimale Punktzahl zum Bestehen wird in den Lektionseinstellungen festgelegt.
                </Alert>
            )}

            <QuizGenerator
                open={generatorOpen}
                onClose={() => setGeneratorOpen(false)}
                onQuestionsGenerated={handleQuestionsGenerated}
                defaultQuestionType="drag_drop"
            />
        </Box>
    );
};

export default DragDropEditor;
